import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getPhoneNumber from "@WhiteCap/Handlers/SubscriptionsDashboard/GetPhoneNumber";
import {
    SubscribedEmailDto,
    SubscriptionsModalType,
} from "@WhiteCap/Store/Reducer/WhiteCapReducer/OsnSubscriptionsDashboard/SubscriptionsDashboard";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: AddSubscribersStyles;
    addSubscriber: (email: string, onSuccessCallback: () => void) => void;
    emails?: SubscribedEmailDto[];
    subscriptionModalType: SubscriptionsModalType;
}

const mapDispatchToProps = {
    getPhoneNumber,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface AddSubscribersStyles {
    container?: InjectableCss;
    sectionHeading?: TypographyProps;
    wrapper?: InjectableCss;
    addBtn?: ButtonPresentationProps;
    emailTextField?: TextFieldPresentationProps;
    textFieldRow?: InjectableCss;
    suggestionText?: TypographyProps;
}

export const addSubscribersStyles: AddSubscribersStyles = {
    container: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 50px;
                        `,
                        null,
                        css`
                            margin-bottom: 60px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    sectionHeading: {
        variant: "h4",
        weight: 325,
        lineHeight: "normal",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            margin-bottom: 8px;
                        `,
                        null,
                        css`
                            font-size: 24px;
                            margin-bottom: 21px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            gap: 10px;
            flex-direction: column;
        `,
    },
    emailTextField: {
        cssOverrides: {
            formField: css`
                max-width: 479px;
                flex-grow: 1;
            `,
        },
    },
    addBtn: {
        variant: "primary",
        css: css`
            max-height: 40px;
        `,
    },
    textFieldRow: {
        css: css`
            display: flex;
            gap: 22px;
        `,
    },
    suggestionText: {
        size: 12,
    },
};

const styles = addSubscribersStyles;

const AddSubscribers = ({ addSubscriber, emails, getPhoneNumber, subscriptionModalType }: Props) => {
    const emailInvalidMessage = siteMessage("OSN_Add_Subscriber_Invalid_Email_Message");
    const emailRequiredMessage = siteMessage("OSN_Add_subscriber_Email_Required_Message");
    const emailAlreadyExistsMessage = siteMessage("OSN_Email_Exists_Message");
    const oneOfTheEmailsAlreadyExistsMessage = siteMessage("OSN_One_Of_The_Email_Exists_Message");
    const emailPlaceholder = siteMessage("OSN_Add_Subscribers_Modal_Email_Field_Placeholder");
    const suggestionMessage = siteMessage("OSN_Add_Subscribers_Modal_Email_Field_Suggestion_Message");
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const onSuccessCallback = () => {
        // Restore to initial state
        setEmail("");
        setEmailError("emailRequiredMessage");
        setIsSubmitted(false);
    };

    const handleAddSubscriber = () => {
        if (!isSubmitted) {
            setIsSubmitted(true);
        }
        if (emailError) {
            return;
        }
        const commaSeparatedEmails = email.split(",");
        commaSeparatedEmails.forEach(emailStr => {
            addSubscriber(emailStr, onSuccessCallback);
        });
        getPhoneNumber({
            emailId: email.split(" ").join(""),
            subscriptionModalType,
            onError: (message: string) => {
                toasterContext.addToast({
                    body: message,
                    messageType: "danger",
                });
            },
        });
    };

    const emailChangeHandler = (value: string) => {
        const emailRegex =
            /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})+(?:,([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}))*$/;
        value = value.split(" ").join("");
        let errorMessage = !value ? emailRequiredMessage : emailRegex.test(value) ? "" : emailInvalidMessage;
        const commaSeparatedEmails = value.split(",");
        let emailAlreadyExists = false;
        commaSeparatedEmails.forEach(email => {
            if (emails && emails?.findIndex(emailObj => emailObj.email === email) !== -1) {
                emailAlreadyExists = true;
            }
        });
        if (emailAlreadyExists) {
            errorMessage =
                commaSeparatedEmails.length > 1 ? oneOfTheEmailsAlreadyExistsMessage : emailAlreadyExistsMessage;
        }
        setEmailError(errorMessage);
        setEmail(value);
    };

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.sectionHeading}>Add Subscribers</Typography>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.textFieldRow}>
                    <TextField
                        {...styles.emailTextField}
                        type="email"
                        onChange={e => emailChangeHandler(e.currentTarget.value)}
                        value={email}
                        data-test-selector="createAccount_email"
                        maxLength={256}
                        placeholder={emailPlaceholder.toString()}
                        error={isSubmitted && emailError}
                        onKeyPress={e => {
                            if (e.key === "Enter") {
                                handleAddSubscriber();
                            }
                        }}
                    />
                    <Button {...styles.addBtn} onClick={handleAddSubscriber}>
                        {translate("Add")}
                    </Button>
                </StyledWrapper>
                <StyledWrapper>
                    <Typography {...styles.suggestionText}>
                        {suggestionMessage.toString()}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(null, mapDispatchToProps)(AddSubscribers);
